import React, { useEffect, useRef, useState } from "react";

/**
 * InlineCell — a table cell that shows a value and turns into a small
 * inline input/select when clicked. On blur or Enter it commits the change
 * via ``onSave(field, newValue)`` — the caller is responsible for the
 * PATCH round-trip and the state update. Escape cancels.
 *
 * Mar 1 2026 — built for the Procurement inline-edit spec. Kept generic so
 * other modules (Orders, Mapping) can reuse it. Supports:
 *   - type="text"   → plain text input
 *   - type="number" → numeric input, empty string is preserved as "" so
 *                     the backend clears the field instead of writing 0
 *   - type="date"   → HTML <input type="date">
 *   - type="select" → native <select> with ``options`` array
 *
 * Props:
 *   value      — current value (rendered as "—" when null/empty)
 *   type       — one of the four above (default "text")
 *   options    — string[] for type="select"
 *   onSave     — (newValue) => Promise|void — commits the change
 *   testId     — data-testid for the whole cell (both display + editor)
 *   editable   — when false, renders as read-only display (default true)
 *   render     — optional (v) => ReactNode custom renderer for the display
 *                mode. Default is String(value) or "—".
 *   className  — applied to the container <td>
 *   mono       — when true, applies font-mono to the display value
 *   prefix     — optional prefix (e.g. "¥") shown before the value in
 *                display mode; NOT included in the edit input
 */
export default function InlineCell({
  value,
  type = "text",
  options = null,
  onSave,
  testId = null,
  editable = true,
  render = null,
  className = "",
  mono = false,
  prefix = "",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type === "text" || type === "number") {
        try { inputRef.current.select(); } catch { /* ignore */ }
      }
    }
  }, [editing, type]);

  const startEdit = (e) => {
    if (!editable) return;
    e?.stopPropagation();
    setDraft(value === null || value === undefined ? "" : String(value));
    setEditing(true);
  };

  const commit = async () => {
    if (!editing) return;
    // Normalise the outgoing value based on type.
    let out = draft;
    if (type === "number") {
      const trimmed = String(draft).trim();
      out = trimmed === "" ? "" : Number(trimmed);
      if (typeof out === "number" && Number.isNaN(out)) out = "";
    }
    const before = value === null || value === undefined ? "" : String(value);
    // Skip network round-trip when the value didn't actually change.
    if (String(out) === before) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave?.(out);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => setEditing(false);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && type !== "select") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  if (!editing) {
    const isEmpty = value === null || value === undefined || value === "";
    const display = render
      ? render(value)
      : isEmpty
        ? <span className="text-slate-400">—</span>
        : <>{prefix}{String(value)}</>;
    return (
      <td
        onClick={startEdit}
        data-testid={testId}
        className={`${mono ? "font-mono " : ""}${editable ? "cursor-text hover:bg-blue-50/60 " : ""}${className}`}
        title={editable ? "Click to edit" : undefined}
      >
        {display}
      </td>
    );
  }

  const commonProps = {
    ref: inputRef,
    disabled: saving,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown,
    onClick: (e) => e.stopPropagation(),
    "data-testid": testId ? `${testId}-input` : undefined,
    className: "h-6 w-full min-w-[80px] px-1 text-[12px] border border-blue-400 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white",
  };

  return (
    <td className={className} onClick={(e) => e.stopPropagation()}>
      {type === "select" ? (
        <select {...commonProps}>
          <option value="">—</option>
          {(options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : type === "date" ? (
        <input type="date" {...commonProps} />
      ) : type === "number" ? (
        <input type="number" step="any" {...commonProps} />
      ) : (
        <input type="text" {...commonProps} />
      )}
    </td>
  );
}
